import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends Component {
  /**
   * The observer that triggers loading once the section scrolls into view
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    // Already rendered server-side (e.g. theme editor preview), nothing to fetch.
    if (this.dataset.recentlyViewedPerformed === 'true') {
      return;
    }

    // Nothing has been viewed yet on this browser, so there's nothing to fetch or show.
    if (RecentlyViewed.getProducts().length === 0) {
      this.#handleEmpty();
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Fetches and renders the recently viewed products by re-rendering this section
   * against a search request scoped to the locally stored product ids.
   */
  async #loadRecentlyViewed() {
    const { sectionId, url } = this.dataset;
    const viewedProductIds = RecentlyViewed.getProducts();

    if (!sectionId || !url || viewedProductIds.length === 0) {
      this.#handleEmpty();
      return;
    }

    const searchUrl = new URL(url, window.location.origin);
    searchUrl.searchParams.set('q', viewedProductIds.map((id) => `id:${id}`).join(' OR '));
    searchUrl.searchParams.set('resources[type]', 'product');

    try {
      await sectionRenderer.renderSection(sectionId, { url: searchUrl, mode: 'full', injectStylesheet: true });
    } catch (error) {
      this.#handleError(error);
    }
  }

  #handleEmpty() {
    this.classList.add('hidden');
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
